import React from 'react';
import PropTypes from 'prop-types';
import KeyCodes from 'library/helpers/key-codes';
import { withA11y } from 'util/hoc';
import { resolve } from 'library/helpers/path-resolver';
import MonthSelect from 'library/components/form-elements/month-dropdown';
import YearSelect from 'library/components/form-elements/year-dropdown';
import Input from 'library/components/form-components/input';
import {
  StyledFormContent,
  StyledFormRow,
  StyledSpacer,
  ValueContainer,
} from 'apps/journey/pages/user-dashboard/components/work-experience/styled-work-experience';
import { Dropdown, Textarea } from 'library/components/form-components';
import SyntheticEvent from 'library/helpers/synthetic-events';

const educationDegrees = [
  {
    value: 'high-school',
    label: 'High School',
    className: 'dropdownOptionClass',
  },
  {
    value: 'associates',
    label: 'Associates',
    className: 'dropdownOptionClass',
  },
  {
    value: 'bachelors',
    label: 'Bachelors',
    className: 'dropdownOptionClass',
  },
  {
    value: 'masters',
    label: 'Masters',
    className: 'dropdownOptionClass',
  },
  {
    value: 'doctorate',
    label: 'Doctorate',
    className: 'dropdownOptionClass',
  },
];

class EducationExperience extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { hoverState: 'off' };
    this.form = React.createRef();
    this.toggle = React.createRef();
    this.formContainer = this.props.innerRef || React.createRef();
  }

  handleClick = (e) => {
    if (
      !this.formContainer?.current?.contains(e.target) &&
      this.formContainer?.current !== e.target &&
      typeof e.target?.className === 'string' &&
      e.target.className.indexOf('ValueContainer') === -1 &&
      e.target.className.indexOf('dropdownOptionClass') === -1
    ) {
      e.stopPropagation();
      e.preventDefault();
      this.setState({ hoverState: 'off' });
      this.props.onEdit(false);
    }
  };

  handleEditState = () => {
    this.setState({ hoverState: 'edit' });
    this.props.onEdit(true);
  };

  handleKeyPress = (e, actionCallback, bypassFormCheck) => {
    if (
      (e.target === this.formContainer?.current || bypassFormCheck) &&
      KeyCodes.POSITIVE_AFFIRMATIONS.includes(e.which)
    ) {
      actionCallback();
      e.preventDefault();
      e.stopPropagation();
    }
  };

  componentDidMount() {
    mount.addEventListener('click', this.handleClick);
  }

  componentWillUnmount() {
    mount.removeEventListener('click', this.handleClick);
  }

  handleEditOnKeyPress = (e) => {
    if (this.state.hoverState !== 'edit')
      this.handleKeyPress(e, this.handleEditState, true);
  };

  render() {
    const { date_range, onChange, isTabbing, savedFields, i } = this.props;
    const { hoverState } = this.state;

    const [start_year, start_month] = resolve(date_range, 'start', '--').split(
      '-'
    );
    const [finish_year, finish_month] = resolve(
      date_range,
      'finish',
      '--'
    ).split('-');

    return (
      <StyledFormContent
        ref={this.formContainer}
        onMouseEnter={() =>
          hoverState !== 'edit' && this.setState({ hoverState: 'on' })
        }
        onMouseLeave={() =>
          hoverState !== 'edit' && this.setState({ hoverState: 'off' })
        }
        onFocus={() => this.props.onEdit(true)}
        onClick={this.handleEditState}
        isTabbing={isTabbing}
        tabIndex={0}
        aria-label="Education History form"
        onKeyPress={this.handleEditOnKeyPress}
      >
        <StyledFormRow>
          {this.props.school || hoverState === 'edit' ? (
            <Input
              label="School"
              customAriaLabel="School"
              type="text"
              placeholder="School"
              name="school"
              onChange={onChange}
              value={resolve(this.props, 'school', '')}
              saved={savedFields.includes(`education_histories-${i}-school`)}
            />
          ) : (
            <ValueContainer
              tabIndex={0}
              onKeyPress={this.handleEditOnKeyPress}
              hover={hoverState === 'on'}
              aria-label="Press to edit school name"
            >
              {hoverState === 'on' && 'School'}
            </ValueContainer>
          )}
        </StyledFormRow>
        <StyledFormRow>
          {this.props.degree || hoverState === 'edit' ? (
            <Dropdown
              name="degree"
              label="Degree"
              aria-label="Degree"
              placeholder={
                educationDegrees.find(
                  ({ value }) => value === this.props?.degree
                )?.label || 'Degree'
              }
              options={educationDegrees}
              onChange={(e) => {
                onChange(
                  new SyntheticEvent({
                    name: 'degree',
                    value: e.currentTarget?.getAttribute('value'),
                  })
                );
              }}
              value={resolve(this.props, 'degree', '')}
              carrot
              scrollOutOffsetTop={150}
              saved={savedFields.includes(`education_histories-${i}-degree`)}
            />
          ) : (
            <ValueContainer
              tabIndex={0}
              onKeyPress={this.handleEditOnKeyPress}
              hover={hoverState === 'on'}
              aria-label="Press to edit degree"
            >
              {hoverState === 'on' && 'Degree'}
            </ValueContainer>
          )}
        </StyledFormRow>
        <StyledFormRow>
          {this.props.field_of_study || hoverState === 'edit' ? (
            <Input
              label="Field of Study"
              customAriaLabel="Field of Study"
              type="text"
              placeholder="Field of Study"
              name="field_of_study"
              onChange={onChange}
              value={resolve(this.props, 'field_of_study', '')}
              saved={savedFields.includes(
                `education_histories-${i}-field_of_study`
              )}
            />
          ) : (
            <ValueContainer
              tabIndex={0}
              onKeyPress={this.handleEditOnKeyPress}
              hover={hoverState === 'on'}
              aria-label="Press to edit field of study"
            >
              {hoverState === 'on' && 'Field of Study'}
            </ValueContainer>
          )}
        </StyledFormRow>
        <StyledFormRow>
          {finish_month || finish_year || hoverState === 'edit' ? (
            <>
              <MonthSelect
                onChange={onChange}
                label="Finish Month"
                placeholder="End Month"
                aria-label="finish_month"
                name="finish_month"
                value={finish_month}
                saved={savedFields.includes(
                  `education_histories-${i}-finish_month`
                )}
              />
              <StyledSpacer />
              <YearSelect
                onChange={onChange}
                label="Finish Year"
                placeholder="End Year"
                aria-label="finish_year"
                name="finish_year"
                value={finish_year}
                saved={savedFields.includes(
                  `education_histories-${i}-finish_year`
                )}
              />
            </>
          ) : (
            <>
              <ValueContainer
                tabIndex={0}
                onKeyPress={this.handleEditOnKeyPress}
                hover={hoverState === 'on'}
                aria-label="Press to edit finish month"
              >
                {hoverState === 'on' && 'Finish Month'}
              </ValueContainer>
              <StyledSpacer />
              <ValueContainer
                tabIndex={0}
                onKeyPress={this.handleEditOnKeyPress}
                hover={hoverState === 'on'}
                aria-label="Press to edit finish year"
              >
                {hoverState === 'on' && 'Finish Year'}
              </ValueContainer>
            </>
          )}
        </StyledFormRow>
        <StyledFormRow>
          {this.props.description || hoverState === 'edit' ? (
            <Textarea
              label="Description"
              aria-label="this is a text area to describe your experience"
              onChange={onChange}
              value={resolve(this.props, 'description', '')}
              name="description"
              rows={9}
              saved={savedFields.includes(
                `education_histories-${i}-description`
              )}
            />
          ) : (
            <ValueContainer
              tabIndex={0}
              onKeyPress={this.handleEditOnKeyPress}
              textbox
              hover={hoverState === 'on'}
              aria-label="Press to edit description"
            >
              {hoverState === 'on' && 'Description'}
            </ValueContainer>
          )}
        </StyledFormRow>
      </StyledFormContent>
    );
  }
}

EducationExperience.propTypes = {
  onChange: PropTypes.func,
  onEdit: PropTypes.func,
  school: PropTypes.string,
  date_range: PropTypes.object,
  description: PropTypes.string,
  id: PropTypes.number,
  is_current: PropTypes.bool,
  state: PropTypes.string,
  active: PropTypes.bool,
  degree: PropTypes.string,
  field_of_study: PropTypes.string,
  isTabbing: PropTypes.bool,
  innerRef: PropTypes.object,
  savedFields: PropTypes.array,
};

EducationExperience.defaultProps = {
  savedFields: [],
};

export default withA11y(EducationExperience);
